"use client";
import Link from "next/link";
import { useRef } from "react";

const ALPHABETS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

interface Props {
  activeSlug: string;
  merchantCount: number;
}

export default function AlphabetNav({ activeSlug, merchantCount }: Props) {
  const displayLetter = activeSlug === "other" ? "#" : activeSlug.toUpperCase();

  /* ── drag-to-scroll refs (mobile) ── */
  const scrollRef   = useRef<HTMLDivElement>(null);
  const isDragging  = useRef(false);
  const startX      = useRef(0);
  const scrollLeft  = useRef(0);

  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current     = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    if (scrollRef.current) scrollRef.current.style.cursor = "grabbing";
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x    = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.2;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };
  const stopDrag = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = "grab";
  };

  return (
    <div className="bg-white border-b border-slate-100 sticky top-[62px] z-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-10 flex items-center justify-center h-[52px]">

        {/* ── Mobile: drag/swipe scrollable strip ── */}
        <div className="relative flex md:hidden w-full">
          {/* left fade */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent z-10" />
          {/* right fade */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent z-10" />

          <div
            ref={scrollRef}
            className="flex items-center gap-0.5 overflow-x-scroll w-full py-1.5 px-2 select-none"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              cursor: "grab",
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >
            {ALPHABETS.concat("#").map((alpha) => {
              const linkSlug = alpha === "#" ? "other" : alpha;
              const isActive = activeSlug.toUpperCase() === linkSlug.toUpperCase();
              return (
                <Link
                  key={alpha}
                  href={`/all-stores/${linkSlug}`}
                  draggable={false}
                  className={`no-underline flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-black transition-all duration-200 active:scale-90
                    ${isActive
                      ? "bg-indigo-950 text-white shadow-sm"
                      : "text-slate-400 hover:bg-indigo-950 hover:text-white"
                    }`}
                >
                  {alpha}
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Desktop: original expanding pill — completely unchanged ── */}
        <div className="hidden md:flex items-center justify-center w-full">
          <div className="group flex items-center justify-center">
            <div
              className="flex items-center overflow-hidden rounded-full border border-slate-200 shadow-sm bg-white
                transition-all duration-700 ease-[cubic-bezier(0.23,1,0.32,1)]
                w-12 group-hover:w-[840px] h-12 px-2 gap-0.5"
            >
              {/* active letter chip */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-950 flex items-center justify-center shadow-sm">
                <span className="text-white text-[12px] font-black leading-none">{displayLetter}</span>
              </div>

              {/* divider */}
              <div className="flex-shrink-0 w-px h-4 bg-slate-200 mx-1
                opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-300" />

              {/* other letters */}
              {ALPHABETS.concat("#").map((alpha) => {
                const linkSlug = alpha === "#" ? "other" : alpha;
                const isActive = activeSlug.toUpperCase() === linkSlug.toUpperCase();
                if (isActive) return null;
                return (
                  <Link
                    key={alpha}
                    href={`/all-stores/${linkSlug}`}
                    className="no-underline flex-shrink-0 w-7 h-7 flex items-center justify-center
                      rounded-full text-[11px] font-black text-slate-400
                      hover:bg-indigo-950 hover:text-white
                      opacity-0 group-hover:opacity-100
                      transition-all duration-200 delay-200
                      active:scale-90"
                  >
                    {alpha}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
